import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Container,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const AUTH_TOKEN = "7633d6c7-1111-4fd9-b78c-14e0312957f8";

// Styled Typography for the title
const StyledTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontWeight: "bold",
  color: theme.palette.primary.main,
  textAlign: "center",
  fontSize: "2rem",
}));

// Styled Box for content
const StyledContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: "0px 8px 15px rgba(0, 0, 0, 0.1)",
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "50vh",
}));

const PrivacyPolicy = () => {
  const [loading, setLoading] = useState(true);
  const [privacyPolicy, setPrivacyPolicy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        const response = await fetch(`${BASE_URL}/privacy-policy`, {
          headers: {
            authorization: AUTH_TOKEN,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch privacy policy.");
        }

        const data = await response.json();
        if (data.status) {
          setPrivacyPolicy(data.data);
        } else {
          throw new Error("Privacy policy data not available.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyPolicy();
  }, []);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <LoadingContainer>
        <Typography color="error">{error}</Typography>
      </LoadingContainer>
    );
  }

  return (
    <Container maxWidth="md">
      <StyledContent>
        <StyledTitle>{privacyPolicy?.title}</StyledTitle>
        <Typography
          variant="body1"
          color="text.secondary"
          mb={3}
          sx={{ textAlign: "justify" }}
        >
          {privacyPolicy?.page_description}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {/* Render HTML Content */}
        <Box
          dangerouslySetInnerHTML={{ __html: privacyPolicy?.description }}
          sx={{
            "& h2": { fontSize: "1.8rem", marginTop: 2, color: "primary.main" },
            "& h3": { fontSize: "1.5rem", marginTop: 2, color: "secondary.main" },
            "& p": { marginBottom: 2, lineHeight: 1.7, textAlign: "justify" },
            "& ul": { paddingLeft: 4, listStyleType: "disc", marginBottom: 2 },
            "& li": { marginBottom: 1 },
            "& a": { color: "primary.main", textDecoration: "underline" },
            "& img": { maxWidth: "100%", height: "auto" },
          }}
        />
      </StyledContent>
    </Container>
  );
};

export default PrivacyPolicy;